import React from "react"
import { Page } from "@shopify/polaris"
import Loading from "components/widgets/Loading"
import AnalyzeHistorizationAlertBanners from "views/molecules/analyze/alert-banner/AnalyzeHistorizationAlertBanners"
import { useAppContext } from "components/AppContext"
import {
  OBJECTIVES_THEMES,
  PILLAR_ENVIRONMENTAL,
  PILLAR_PROFIT,
  PILLAR_SOCIAL,
} from "utils/constants/pillars"
import { ALERT_BANNER_NO_HISTORIZATION } from "utils/constants/alert_banners/alertBannersConstant"
import withAuthRequired from "../../components/auth/withAuthRequired"
import usePillars from "views/organisms/pillars/usePillars"
import { useParams } from "react-router-dom"
import EnvHistoryAnalyzeOrganism from "views/organisms/canvases/charts/analyzes/historization/EnvHistoryAnalyzeOrganism"
import ProfitHistoryAnalyzeOrganism from "views/organisms/canvases/charts/analyzes/historization/ProfitHistoryAnalyzeOrganism"
import TrendChartOrganism from "views/organisms/canvases/charts/analyzes/historization/TrendChartOrganism"
import useAvailablePillar from "hooks/useAvailablePillar"
import AnalyzeTemplate from "views/templates/AnalyzeTemplate"
import ThemeTabs from "components/widgets/ThemeTabs"
import AccessModal from "components/AccessModal"

const TREND_THEME_ALIASES = {
  workplace: "dashboard_trend_workplace",
  network: "dashboard_trend_network",
  datacenter_internal: "dashboard_trend_internal_hosting",
}

const TREND_ENABLED_THEMES = Object.keys(TREND_THEME_ALIASES)

const AnalyzeHistorizationPage = () => {
  const {
    parentOrganization,
    historizationRequest,
    currentPeriod,
    envScore,
    isEnvScoreLoading,
    subscription,
  } = useAppContext()

  const hasTrendsAccess = subscription?.features?.trends
  const { pillarId, themeId } = useParams()

  const { data } = historizationRequest

  const availablePillarLoaded = useAvailablePillar({
    pathPrefix: "/analyze/history",
  })

  const hasProfitDisabled =
    (data && !data.profit) || data?.profit?.history_count === 1

  const pillars = usePillars({
    pageName: "/analyze/history",
    [PILLAR_ENVIRONMENTAL]: { disabled: data && !data.env },
    [PILLAR_SOCIAL]: { disabled: true },
    [PILLAR_PROFIT]: { disabled: hasProfitDisabled },
  })

  const isEnvPillar = pillarId === PILLAR_ENVIRONMENTAL

  if (
    historizationRequest.isLoading ||
    !parentOrganization ||
    !availablePillarLoaded ||
    (isEnvPillar && (isEnvScoreLoading || !envScore))
  ) {
    return <Loading />
  }

  const themeAvailability = {
    workplace: +envScore?.odd13_workplace_CO2 > 0,
    network: +envScore?.odd13_network_CO2 > 0,
    datacenter_internal: +envScore?.odd13_datacenter_internal_CO2 > 0,
  }

  const hiddenThemes = OBJECTIVES_THEMES[13].filter(
    (t) => !TREND_ENABLED_THEMES.includes(t) || !themeAvailability[t]
  )

  const objectives = [
    {
      objectiveId: 13,
      selected: true,
      url: `/analyze/history/${PILLAR_ENVIRONMENTAL}/13`,
    },
  ]

  const renderEnvContent = () => {
    if (themeId && TREND_THEME_ALIASES[themeId]) {
      return (
        <TrendChartOrganism
          verdiktAlias={TREND_THEME_ALIASES[themeId]}
          hasTrendsAccess={hasTrendsAccess}
        />
      )
    }
    return <EnvHistoryAnalyzeOrganism data={data.env} />
  }

  const renderEnv = () => (
    <AnalyzeTemplate
      parentOrganization={parentOrganization}
      themeType={PILLAR_ENVIRONMENTAL}
      pillars={pillars}
      objectives={objectives}
      tabsComponent={
        <ThemeTabs
          pageName="analyze/history"
          pillar={PILLAR_ENVIRONMENTAL}
          objective="13"
          theme={themeId}
          hiddenThemes={hiddenThemes}
        />
      }
      periodName={currentPeriod?.name}
    >
      {renderEnvContent()}
    </AnalyzeTemplate>
  )

  const displayPillar = () => {
    if (pillarId === PILLAR_ENVIRONMENTAL && !!data.env) {
      return renderEnv()
    }

    if (pillarId === PILLAR_PROFIT && !!data.profit) {
      return (
        <ProfitHistoryAnalyzeOrganism data={data.profit} pillars={pillars} />
      )
    }

    return "not found"
  }

  return (
    <Page fullWidth={historizationRequest.isSuccess}>
      {historizationRequest.isError && (
        <>
          {historizationRequest.errorCode === 404 && (
            <AnalyzeHistorizationAlertBanners
              type={ALERT_BANNER_NO_HISTORIZATION}
            />
          )}
        </>
      )}

      {historizationRequest.isSuccess && data && displayPillar()}
    </Page>
  )
}

export default withAuthRequired(AnalyzeHistorizationPage)
